import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Free scans an anonymous user gets before being asked to sign up.
// Keep in sync with max_scans in backend check_and_increment_anon_scan.
export const ANON_SCAN_LIMIT = 2;

export class AnonymousScanLimitError extends Error {
  constructor({ message, scansUsed, scanLimit, retryAfterSeconds }) {
    super(message || 'Anonymous scan limit reached. Sign up to keep scanning.');
    this.name = 'AnonymousScanLimitError';
    this.requiresSignup = true;
    this.scansUsed = scansUsed;
    this.scanLimit = scanLimit;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

// Thrown when a signed-up user doesn't have enough credits for a scan (HTTP 402
// with requires_topup). Mirrors AnonymousScanLimitError. Only occurs when the
// backend runs credit enforcement in 'hard' mode.
export class InsufficientCreditsError extends Error {
  constructor({ message, creditsRequired, creditsBalance, bandLabel }) {
    super(message || 'Not enough credits for this scan. Add credits to continue.');
    this.name = 'InsufficientCreditsError';
    this.requiresTopup = true;
    this.creditsRequired = creditsRequired;
    this.creditsBalance = creditsBalance;
    this.bandLabel = bandLabel;
  }
}

// Normalize a thrown error (fetch-based InsufficientCreditsError or an axios 402)
// into an InsufficientCreditsError, or return null if it isn't a credit error.
export const asInsufficientCreditsError = (error) => {
  if (error instanceof InsufficientCreditsError) {
    return error;
  }
  // Already-flattened rejection payload (from a Redux thunk's rejectWithValue).
  if (error?.requiresTopup) {
    return new InsufficientCreditsError({
      message: error.message,
      creditsRequired: error.creditsRequired,
      creditsBalance: error.creditsBalance,
      bandLabel: error.bandLabel,
    });
  }
  const resp = error?.response;
  const body = resp?.data?.data;
  if (resp?.status === 402 && body?.requires_topup) {
    return new InsufficientCreditsError({
      message: resp.data.message,
      creditsRequired: body.credits_required,
      creditsBalance: body.credits_balance,
      bandLabel: body.band_label,
    });
  }
  return null;
};

// Normalize a thrown error (fetch-based AnonymousScanLimitError or an axios 429)
// into an AnonymousScanLimitError, or return null if it isn't a limit error.
export const asAnonLimitError = (error) => {
  if (error instanceof AnonymousScanLimitError) {
    return error;
  }
  const resp = error?.response;
  const body = resp?.data?.data;
  if (resp?.status === 429 && body?.requires_signup) {
    return new AnonymousScanLimitError({
      message: resp.data.message,
      scansUsed: body.scans_used,
      scanLimit: body.scan_limit,
      retryAfterSeconds: body.retry_after_seconds,
    });
  }
  return null;
};

const checkCompliance = async (payload, authToken) => {
  const { platform, transcript, videoUrl, title } = payload;

  let body;
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  if (videoUrl) {
    body = JSON.stringify({
      platform,
      video_url: videoUrl,
      title: title || undefined,
    });
  } else if (transcript) {
    const trimmedTranscript = transcript.trim();
    body = JSON.stringify({
      platform,
      transcript: trimmedTranscript,
      title: title || undefined,
    });
  } else {
    throw new Error('No content (transcript or video) provided for compliance check.');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/violation-checker`, {
      method: 'POST',
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 429 && errorData?.data?.requires_signup) {
        throw new AnonymousScanLimitError({
          message: errorData.message,
          scansUsed: errorData.data.scans_used,
          scanLimit: errorData.data.scan_limit,
          retryAfterSeconds: errorData.data.retry_after_seconds,
        });
      }
      if (response.status === 402 && errorData?.data?.requires_topup) {
        throw new InsufficientCreditsError({
          message: errorData.message,
          creditsRequired: errorData.data.credits_required,
          creditsBalance: errorData.data.credits_balance,
          bandLabel: errorData.data.band_label,
        });
      }
      throw new Error(errorData.message || 'Failed to check compliance');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[Service] Error checking compliance:', error);
    throw error;
  }
};

const createToken = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/create-token`
    );
    return response.data; // Assuming the token is directly in response.data or response.data.token
  } catch (error) {
    console.error('[Service] Error creating token:', error);
    throw error;
  }
};

const startMultipartUpload = async (fileName, fileType, fileSize, authToken) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/start_multipart_upload`,
    {
      file_name: fileName,
      file_type: fileType,
      file_size: fileSize,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  return response.data;
};

const generatePartUrls = async (fileName, uploadId, partNumbers, authToken) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/generate_part_urls`,
    {
      file_name: fileName,
      upload_id: uploadId,
      part_numbers: partNumbers,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  return response.data;
};

const uploadMultipartChunkToPresignedUrl = async (url, chunk, partNumber, onUploadProgress) => {
  try {
    const response = await axios.put(url, chunk, {
      headers: {
        'Content-Type': 'application/octet-stream', // Required for S3 multipart upload parts
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onUploadProgress({ partNumber, progress: percentCompleted });
      },
    });
    return response; // Return the full response to get ETag from headers
  } catch (error) {
    console.error(`[Service] Error uploading part ${partNumber}:`, error);
    throw error;
  }
};

const completeMultipartUpload = async (fileName, uploadId, parts, authToken, title = null) => {
  const body = {
    file_name: fileName,
    upload_id: uploadId,
    parts: parts,
  };
  if (title) {
    body.title = title;
  }
  const response = await axios.post(
    `${API_BASE_URL}/api/complete_multipart_upload`,
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  return response.data;
};

const getJobStatus = async (jobId, authToken) => {
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/job-status/${jobId}`,
      {
        headers: headers,
      }
    );
    return response.data;
  } catch (error) {
    console.error(`[Service] Error getting job status for job ID ${jobId}:`, error);
    throw error;
  }
};

const createJob = async (fileName, platform, title, authToken) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/create-job`,
    {
      file_name: fileName,
      platform: platform,
      title: title || '',
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  return response.data;
};

const listJobs = async (authToken, { limit = 10, cursor = null, sortOrder = 'desc' } = {}) => {
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  // Build query parameters
  const params = {
    limit,
    sort_order: sortOrder,
  };
  if (cursor) {
    params.cursor = cursor;
  }

  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/list-jobs`,
      {
        headers: headers,
        params: params,
      }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error listing jobs:', error);
    throw error;
  }
};

const getGuidelines = async (platform = 'all') => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/guidelines`,
      {
        params: { platform },
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error getting guidelines:', error);
    throw error;
  }
};

const updateJobTitle = async (jobId, title, authToken) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/update-job-title`,
      {
        job_id: jobId,
        title: title,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error updating job title:', error);
    throw error;
  }
};

// Content Library API methods

const listContent = async (authToken, { limit = 50, cursor = null, sortOrder = 'desc', sortBy = 'created' } = {}) => {
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const params = { limit, sort_order: sortOrder, sort_by: sortBy };
  if (cursor) {
    params.cursor = cursor;
  }

  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/content`,
      { headers, params }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error listing content:', error);
    throw error;
  }
};

const deleteContent = async (contentId, authToken) => {
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/content/${contentId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error deleting content:', error);
    throw error;
  }
};

// Fetch a single content row. A pure read with no side effects, which matters
// for polling: unlike GET /api/scan/cost it does not re-trigger the thumbnail
// Lambda on each call, so it can be polled without piling up duplicate jobs.
const getContent = async (contentId, authToken) => {
  const headers = { 'Content-Type': 'application/json' };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  const response = await axios.get(`${API_BASE_URL}/api/content/${contentId}`, { headers });
  return response.data;
};

const scanContent = async (contentId, platform, authToken) => {
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/content/${contentId}/scan`,
      { platform },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error scanning content:', error);
    throw error;
  }
};

// Dashboard API methods

const getDashboardStats = async (authToken) => {
  let headers = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/dashboard/stats`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('[Service] Error getting dashboard stats:', error);
    throw error;
  }
};

const contentComplianceService = {
  checkCompliance,
  createToken,
  startMultipartUpload,
  generatePartUrls,
  uploadMultipartChunkToPresignedUrl,
  completeMultipartUpload,
  createJob,
  getJobStatus,
  listJobs,
  getGuidelines,
  updateJobTitle,
  listContent,
  deleteContent,
  getContent,
  scanContent,
  getDashboardStats,
};

export default contentComplianceService;
